/**
 * sz-26-purchase-guide — single-page purchase quiz wizard
 * Steps: 0 hero → 1–5 questions → 6 results
 */
(function () {
  'use strict';

  function parseList(value) {
    if (!value) return [];
    return String(value)
      .split(',')
      .map(function (s) {
        return s.trim().toLowerCase();
      })
      .filter(Boolean);
  }

  function resolveBudget(q5, budgets, subunit) {
    var factor = subunit || 100;
    var range = (q5 && budgets && budgets[q5]) || null;
    var minCents = range && range.min_cents != null ? Number(range.min_cents) : null;
    var maxCents = range && range.max_cents != null ? Number(range.max_cents) : null;
    if (minCents == null && maxCents == null) {
      var fallback = {
        under400: { max: 399 },
        mid400_700: { min: 400, max: 700 },
        high700_1500: { min: 700, max: 1500 }
      }[q5];
      if (!fallback) return null;
      minCents = fallback.min != null ? fallback.min * factor : null;
      maxCents = fallback.max != null ? fallback.max * factor : null;
    }
    if (minCents == null && maxCents == null) return null;
    return { min_cents: minCents, max_cents: maxCents };
  }

  function machinePriceCents(machine, subunit) {
    var factor = subunit || 100;
    var fromVariant = null;
    if (machine.price_cents != null && machine.price_cents !== '') {
      var variantPrice = Number(machine.price_cents);
      if (!isNaN(variantPrice) && variantPrice > 0) fromVariant = variantPrice;
    }
    var fromText = null;
    var match = String(machine.price_text || '')
      .replace(/,/g, '')
      .match(/(\d+(?:\.\d+)?)/);
    if (match) fromText = Math.round(parseFloat(match[1]) * factor);
    if (fromVariant == null) return fromText;
    if (fromText != null && fromText > fromVariant) return fromText;
    return fromVariant;
  }

  function profileScore(machine, answers) {
    var matches = 0;
    var mismatches = 0;
    for (var i = 1; i <= 4; i++) {
      var key = 'q' + i;
      var allowed = parseList(machine[key]);
      if (!allowed.length) continue;
      var answer = (answers[key] || '').toLowerCase();
      if (answer && allowed.indexOf(answer) !== -1) {
        matches += 1;
      } else {
        mismatches += 1;
      }
    }
    return {
      matches: matches,
      mismatches: mismatches,
      score: matches * 3 - mismatches * 2
    };
  }

  function inBudget(priceCents, range) {
    if (!range || priceCents == null) return null;
    var hasBound = range.min_cents != null || range.max_cents != null;
    if (!hasBound) return null;
    if (range.min_cents != null && priceCents < range.min_cents) return false;
    if (range.max_cents != null && priceCents > range.max_cents) return false;
    return true;
  }

  function budgetDistance(priceCents, range) {
    if (priceCents == null || !range) return 1e15;
    var hasBound = range.min_cents != null || range.max_cents != null;
    if (!hasBound) return 0;
    if (range.min_cents != null && priceCents < range.min_cents) {
      return range.min_cents - priceCents;
    }
    if (range.max_cents != null && priceCents > range.max_cents) {
      return priceCents - range.max_cents;
    }
    return 0;
  }

  function pickMachines(machines, answers, budgets, limit, subunit) {
    limit = limit || 2;
    budgets = budgets || {};

    var q5 = (answers.q5 || '').toLowerCase();
    var range = resolveBudget(q5, budgets, subunit);

    var scored = machines.map(function (machine, idx) {
      var profile = profileScore(machine, answers);
      var price = machinePriceCents(machine, subunit);
      var fit = inBudget(price, range);
      var dist = budgetDistance(price, range);
      var allowedQ5 = parseList(machine.q5);
      var tagBonus = q5 && allowedQ5.length && allowedQ5.indexOf(q5) !== -1 ? 1 : 0;
      return {
        machine: machine,
        idx: idx,
        inBudget: fit === true,
        unknownBudget: fit === null,
        dist: dist,
        tagBonus: tagBonus,
        profileMatches: profile.matches,
        profileMismatches: profile.mismatches,
        profileScore: profile.score
      };
    });

    scored.sort(function (a, b) {
      if (a.profileMismatches !== b.profileMismatches) {
        return a.profileMismatches - b.profileMismatches;
      }
      if (a.profileMatches !== b.profileMatches) return b.profileMatches - a.profileMatches;
      if (a.inBudget !== b.inBudget) return a.inBudget ? -1 : 1;
      if (a.dist !== b.dist) return a.dist - b.dist;
      if (a.tagBonus !== b.tagBonus) return b.tagBonus - a.tagBonus;
      return a.idx - b.idx;
    });

    function rowIsFull(row) {
      // 文案只在「真有妥协」时出现，软匹配（如 3 命中 2）不算
      if (q5 && row.inBudget === false) return false;
      if (row.profileMatches === 0) return false;
      return true;
    }

    var shown = scored.slice(0, limit);

    return {
      rows: shown.map(function (row) {
        return {
          machine: row.machine,
          full: rowIsFull(row)
        };
      })
    };
  }

  function money(cents, format) {
    if (cents == null || cents === '') return '';
    format = format || {};
    var factor = format.subunit || 100;
    var amount = Number(cents) / factor;
    var digits = factor === 1000 ? 3 : 2;
    var text;
    if (factor === 1 || Math.abs(amount - Math.round(amount)) < 1e-8) {
      text = String(Math.round(amount));
    } else {
      text = amount.toFixed(digits);
    }
    var symbol = format.symbol || '';
    var gap = format.space ? ' ' : '';
    if (symbol) {
      if (format.position === 'after') return text + gap + symbol;
      return symbol + gap + text;
    }
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: format.currency || 'USD'
      }).format(amount);
    } catch (e) {
      return '$' + text;
    }
  }

  function initRoot(root) {
    if (!root || root.dataset.pgReady === '1') return;
    root.dataset.pgReady = '1';

    var dataEl = root.querySelector('[data-pg-machines]');
    var machines = [];
    try {
      machines = JSON.parse(dataEl ? dataEl.textContent : '[]');
    } catch (e) {
      machines = [];
    }

    var budgetsEl = root.querySelector('[data-pg-budgets]');
    var budgets = {};
    var subunit = 100;
    try {
      budgets = JSON.parse(budgetsEl ? budgetsEl.textContent : '{}') || {};
    } catch (e) {
      budgets = {};
    }
    if (budgetsEl && budgetsEl.getAttribute('data-currency-subunit')) {
      var parsedSubunit = Number(budgetsEl.getAttribute('data-currency-subunit'));
      if (parsedSubunit === 1 || parsedSubunit === 100 || parsedSubunit === 1000) subunit = parsedSubunit;
    }

    var state = {
      step: 0,
      answers: {},
      selectedMachineId: null,
      selectedAddons: {}
    };

    var panels = root.querySelectorAll('[data-pg-panel]');
    var stepDots = root.querySelectorAll('[data-pg-step-dot]');
    var backBtn = root.querySelector('[data-pg-back]');
    var nextBtn = root.querySelector('[data-pg-next]');
    var footer = root.querySelector('[data-pg-footer]');
    var resultTitle = root.querySelector('[data-pg-result-title]');
    var resultGrid = root.querySelector('[data-pg-result-grid]');
    var resultDots = root.querySelector('[data-pg-result-dots]');
    var startLabel = root.dataset.startLabel || 'Get Started';
    var nextLabel = root.dataset.nextLabel || 'Next';
    var finishLabel = root.dataset.finishLabel || 'See Results';
    var moneyFormat = {
      subunit: subunit,
      prefix: root.dataset.pricePrefix || '',
      suffix: root.dataset.priceSuffix || '',
      style: root.dataset.amountStyle || 'amount'
    };

    function currentAnswer(step) {
      return state.answers['q' + step] || '';
    }

    function syncOptionCards(step) {
      var panel = root.querySelector('[data-pg-panel="' + step + '"]');
      if (!panel) return;
      var cards = panel.querySelectorAll('[data-pg-option]');
      var selected = currentAnswer(step);
      cards.forEach(function (card) {
        var id = card.getAttribute('data-pg-option') || '';
        var on = id && id === selected;
        card.classList.toggle('is-selected', on);
        card.setAttribute('aria-checked', on ? 'true' : 'false');
      });
    }

    function syncStepper() {
      stepDots.forEach(function (dot) {
        var n = Number(dot.getAttribute('data-pg-step-dot'));
        dot.classList.toggle('is-current', state.step === n);
        dot.classList.toggle('is-done', state.step > n);
      });
    }

    function syncFooter() {
      if (!footer) return;
      var onQuiz = state.step >= 1 && state.step <= 5;
      footer.hidden = !onQuiz;
      root.classList.toggle('is-quiz', onQuiz);
      if (!onQuiz) return;

      if (backBtn) {
        backBtn.hidden = state.step <= 1;
      }
      if (nextBtn) {
        var hasAnswer = !!currentAnswer(state.step);
        nextBtn.disabled = !hasAnswer;
        nextBtn.textContent = state.step === 5 ? finishLabel : nextLabel;
      }
    }

    function setStep(next) {
      state.step = next;
      panels.forEach(function (panel) {
        var id = Number(panel.getAttribute('data-pg-panel'));
        var show = id === state.step;
        panel.hidden = !show;
        panel.classList.toggle('is-active', show);
      });
      syncStepper();
      syncFooter();
      if (state.step >= 1 && state.step <= 5) syncOptionCards(state.step);
      if (state.step === 6) renderResults();
      try {
        root.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } catch (e) {}
    }

    function formatAmount(amount) {
      var style = moneyFormat.style || 'amount';
      var noDecimals = style.indexOf('no_decimals') !== -1;
      var negative = amount < 0;
      var abs = Math.abs(amount);
      var fixed = noDecimals ? String(Math.round(abs)) : abs.toFixed(moneyFormat.subunit === 1000 ? 3 : 2);
      var bits = fixed.split('.');
      var intPart = bits[0];
      var decPart = bits[1] || '';
      var thousands = ',';
      var decimal = '.';
      if (style.indexOf('comma_separator') !== -1) {
        thousands = '.';
        decimal = ',';
      } else if (style.indexOf('apostrophe') !== -1) {
        thousands = "'";
        decimal = '.';
      } else if (style.indexOf('space_separator') !== -1 && style.indexOf('period') === -1) {
        thousands = ' ';
        decimal = ',';
      } else if (style.indexOf('period_and_space') !== -1) {
        thousands = ' ';
        decimal = '.';
      }
      var grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      var text = decPart ? grouped + decimal + decPart : grouped;
      return negative ? '-' + text : text;
    }

    function resultMoney(cents) {
      if (cents == null || cents === '') return '';
      var factor = moneyFormat.subunit || 100;
      var amount = Number(cents) / factor;
      return (moneyFormat.prefix || '') + formatAmount(amount) + (moneyFormat.suffix || '');
    }

    function machineKey(id) {
      return String(id == null ? '' : id);
    }

    function sameMachine(a, b) {
      return machineKey(a) !== '' && machineKey(a) === machineKey(b);
    }

    function findMachine(id) {
      var key = machineKey(id);
      return machines.find(function (item) {
        return machineKey(item.id) === key;
      }) || null;
    }

    function collectAddonsForMachine(machineId) {
      var map = state.selectedAddons[machineKey(machineId)] || {};
      return Object.keys(map)
        .filter(function (k) {
          return map[k];
        })
        .map(Number)
        .filter(function (n) {
          return n === 0 || n > 0;
        });
    }

    function presetAddons(machine) {
      if (!machine) return;
      var preset = {};
      (machine.addons || []).forEach(function (addon, idx) {
        if (addon && addon.title) preset[idx] = true;
      });
      state.selectedAddons[machineKey(machine.id)] = preset;
    }

    function clearMachineAddons(id) {
      if (id == null || id === '') return;
      state.selectedAddons[machineKey(id)] = {};
    }

    function selectMachine(id, options) {
      var nextId = machineKey(id);
      if (!nextId) return;
      options = options || {};
      if (!sameMachine(nextId, state.selectedMachineId)) {
        clearMachineAddons(state.selectedMachineId);
        state.selectedMachineId = nextId;
        var machine = findMachine(nextId);
        if (machine) {
          if (options.addonIndex == null) {
            presetAddons(machine);
          } else {
            state.selectedAddons[nextId] = {};
            state.selectedAddons[nextId][options.addonIndex] = true;
          }
        }
        return;
      }
      if (options.addonIndex != null) {
        if (!state.selectedAddons[nextId]) state.selectedAddons[nextId] = {};
        state.selectedAddons[nextId][options.addonIndex] = options.addonChecked !== false;
      }
    }

    function renderResults() {
      if (!resultGrid) return;
      var pick = pickMachines(machines, state.answers, budgets, 2, subunit);
      var rows = pick.rows || [];
      var picked = rows.map(function (row) {
        return row.machine;
      });
      var fullById = {};
      rows.forEach(function (row) {
        fullById[machineKey(row.machine.id)] = row.full;
      });
      var closestNoteText = root.dataset.closestNote || '';
      if (!state.selectedMachineId && picked.length) {
        state.selectedMachineId = machineKey(picked[0].id);
      }
      if (
        picked.length &&
        !picked.some(function (m) {
          return sameMachine(m.id, state.selectedMachineId);
        })
      ) {
        state.selectedMachineId = machineKey(picked[0].id);
        state.selectedAddons = {};
      }
      state.selectedMachineId = machineKey(state.selectedMachineId);
      picked.forEach(function (machine) {
        var key = machineKey(machine.id);
        if (sameMachine(key, state.selectedMachineId)) {
          if (!state.selectedAddons[key]) presetAddons(machine);
        } else {
          state.selectedAddons[key] = {};
        }
      });

      // 移动端横滑：勾选配件会重绘，保留当前滑到的卡片
      var keepScroll = resultGrid.scrollLeft;
      resultGrid.innerHTML = '';
      var maxAddons = 0;
      picked.forEach(function (machine) {
        var count = (machine.addons || []).filter(function (addon) {
          return addon && addon.title;
        }).length;
        if (count > maxAddons) maxAddons = count;
      });
      var rowSpan = 4 + maxAddons;
      picked.forEach(function (machine) {
        var machineId = machineKey(machine.id);
        var selected = sameMachine(machineId, state.selectedMachineId);
        var addonState = state.selectedAddons[machineId] || {};
        var isFull = fullById[machineId] === true;
        var noteHtml =
          !isFull && closestNoteText
            ? '<p class="sz-pg__machine-note">' + escapeHtml(closestNoteText) + '</p>'
            : '';

        var card = document.createElement('article');
        card.className = 'sz-pg__machine' + (selected ? ' is-selected' : '');
        card.setAttribute('data-pg-machine-card', machineId);
        card.setAttribute('data-pg-select-machine', machineId);
        card.setAttribute('aria-pressed', selected ? 'true' : 'false');
        card.tabIndex = 0;
        card.style.gridRow = 'span ' + rowSpan;

        var nowHtml = '';
        var compareHtml = '';
        if (machine.price_display) {
          nowHtml = '<span class="sz-pg__machine-price">' + escapeHtml(machine.price_display) + '</span>';
          if (machine.compare_display) {
            compareHtml =
              '<span class="sz-pg__machine-compare">' + escapeHtml(machine.compare_display) + '</span>';
          }
        } else if (machine.price_cents != null && machine.price_cents !== '') {
          nowHtml = '<span class="sz-pg__machine-price">' + resultMoney(machine.price_cents) + '</span>';
          if (machine.compare_cents && Number(machine.compare_cents) > Number(machine.price_cents)) {
            compareHtml =
              '<span class="sz-pg__machine-compare">' + resultMoney(machine.compare_cents) + '</span>';
          }
        } else if (machine.price_text) {
          nowHtml = '<span class="sz-pg__machine-price">' + escapeHtml(machine.price_text) + '</span>';
        }
        var stockHtml = '';
        if (machine.variant_id) {
          stockHtml = machine.available
            ? '<span class="sz-pg__stock is-in">' + escapeHtml(root.dataset.stockIn || 'In Stock') + '</span>'
            : '<span class="sz-pg__stock">' + escapeHtml(root.dataset.stockOut || 'Out of Stock') + '</span>';
        }
        var descHtml = machine.description
          ? '<p class="sz-pg__machine-desc">' + escapeHtml(machine.description) + '</p>'
          : '';
        var learnLabel = root.dataset.learnLabel || 'Learn More';
        var learnHtml = escapeHtml(learnLabel);
        if (!/[>›]$/.test(learnLabel)) learnHtml += ' &gt;';

        var addonNodes = '';
        (machine.addons || []).forEach(function (addon, idx) {
          if (!addon || !addon.title) return;
          var checked = selected && !!addonState[idx];
          var addonPrice = '';
          if (addon.price_display) {
            addonPrice +=
              '<span class="sz-pg__addon-price">' + escapeHtml(addon.price_display) + '</span>';
          } else if (addon.price_cents != null && addon.price_cents !== '') {
            addonPrice +=
              '<span class="sz-pg__addon-price">' + resultMoney(addon.price_cents) + '</span>';
          } else if (addon.price_text) {
            addonPrice +=
              '<span class="sz-pg__addon-price">' + escapeHtml(addon.price_text) + '</span>';
          }
          if (addon.compare_display) {
            addonPrice +=
              '<span class="sz-pg__addon-compare">' + escapeHtml(addon.compare_display) + '</span>';
          } else if (addon.compare_cents && Number(addon.compare_cents) > Number(addon.price_cents || 0)) {
            addonPrice +=
              '<span class="sz-pg__addon-compare">' + resultMoney(addon.compare_cents) + '</span>';
          }
          addonNodes +=
            '<label class="sz-pg__addon' +
            (checked ? ' is-checked' : '') +
            '" data-pg-addon-machine="' +
            escapeAttr(machineId) +
            '" data-pg-addon-index="' +
            idx +
            '">' +
            '<input type="checkbox" data-pg-addon="' +
            escapeAttr(machineId) +
            '" data-pg-addon-index="' +
            idx +
            '"' +
            (checked ? ' checked' : '') +
            (addon.variant_id ? ' data-variant-id="' + addon.variant_id + '"' : '') +
            '>' +
            '<span class="sz-pg__check" aria-hidden="true"></span>' +
            '<span class="sz-pg__addon-media"><img src="' +
            escapeAttr(addon.image || '') +
            '" alt="" loading="lazy" width="120" height="120"></span>' +
            '<span class="sz-pg__addon-body"><span class="sz-pg__addon-title">' +
            escapeHtml(addon.title) +
            '</span><span class="sz-pg__addon-desc">' +
            escapeHtml(addon.description || '') +
            '</span></span>' +
            '<span class="sz-pg__addon-prices">' +
            addonPrice +
            '</span>' +
            '</label>';
        });

        var machinePicture =
          '<picture>' +
          (machine.image_mobile
            ? '<source media="(max-width: 768px)" srcset="' + escapeAttr(machine.image_mobile) + '">'
            : '') +
          '<img class="sz-pg__machine-img" src="' +
          escapeAttr(machine.image || '') +
          '" alt="' +
          escapeAttr(machine.title || '') +
          '" loading="lazy" width="588" height="280">' +
          '</picture>';
        card.innerHTML =
          '<div class="sz-pg__machine-media">' +
          machinePicture +
          '<span class="sz-pg__check" aria-hidden="true"></span>' +
          '</div>' +
          '<div class="sz-pg__machine-copy">' +
          '<h3 class="sz-pg__machine-name">' +
          escapeHtml(machine.title || '') +
          '</h3>' +
          noteHtml +
          descHtml +
          '</div>' +
          '<div class="sz-pg__machine-buy">' +
          '<span class="sz-pg__machine-rule"></span>' +
          '<span class="sz-pg__price-row"><span class="sz-pg__price-main">' +
          nowHtml +
          compareHtml +
          '</span>' +
          stockHtml +
          '</span>' +
          (machine.url
            ? '<a class="sz-pg__learn" href="' +
              escapeAttr(machine.url) +
              '" target="_blank" rel="noopener">' +
              learnHtml +
              '</a>'
            : '<span class="sz-pg__learn"></span>') +
          '</div>' +
          '<p class="sz-pg__addons-label">' +
          (addonNodes ? escapeHtml(root.dataset.addonsLabel || 'Suggested Add-ons') : '') +
          '</p>' +
          addonNodes;

        resultGrid.appendChild(card);
      });

      resultGrid.scrollLeft = keepScroll;
      renderResultDots(picked.length);
    }

    function resultSlideIndex() {
      if (!resultGrid) return 0;
      var cards = resultGrid.querySelectorAll('.sz-pg__machine');
      if (cards.length < 2) return 0;
      var step = cards[1].offsetLeft - cards[0].offsetLeft;
      if (!step) return 0;
      var idx = Math.round(resultGrid.scrollLeft / step);
      return Math.max(0, Math.min(cards.length - 1, idx));
    }

    function syncResultDots() {
      if (!resultDots) return;
      var active = resultSlideIndex();
      resultDots.querySelectorAll('[data-pg-result-dot]').forEach(function (dot) {
        var on = Number(dot.getAttribute('data-pg-result-dot')) === active;
        dot.classList.toggle('is-active', on);
        dot.setAttribute('aria-current', on ? 'true' : 'false');
      });
    }

    function renderResultDots(count) {
      if (!resultDots) return;
      resultDots.innerHTML = '';
      resultDots.hidden = count < 2;
      if (count < 2) return;
      for (var i = 0; i < count; i++) {
        var dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'sz-pg__result-dot';
        dot.setAttribute('data-pg-result-dot', String(i));
        dot.setAttribute('aria-label', 'Slide ' + (i + 1));
        resultDots.appendChild(dot);
      }
      syncResultDots();
    }

    if (resultGrid) {
      var dotsTicking = false;
      resultGrid.addEventListener(
        'scroll',
        function () {
          if (dotsTicking) return;
          dotsTicking = true;
          window.requestAnimationFrame(function () {
            dotsTicking = false;
            syncResultDots();
          });
        },
        { passive: true }
      );
    }
    if (resultDots) {
      resultDots.addEventListener('click', function (event) {
        var dot = event.target.closest('[data-pg-result-dot]');
        if (!dot || !resultGrid) return;
        var cards = resultGrid.querySelectorAll('.sz-pg__machine');
        var target = cards[Number(dot.getAttribute('data-pg-result-dot'))];
        if (!target) return;
        resultGrid.scrollTo({ left: target.offsetLeft - cards[0].offsetLeft, behavior: 'smooth' });
      });
    }

    function escapeHtml(str) {
      return String(str || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');
    }

    function escapeAttr(str) {
      return escapeHtml(str).replace(/'/g, '&#39;');
    }

    root.addEventListener('click', function (event) {
      var start = event.target.closest('[data-pg-start]');
      if (start) {
        event.preventDefault();
        setStep(1);
        return;
      }

      var restart = event.target.closest('[data-pg-restart]');
      if (restart) {
        event.preventDefault();
        state.answers = {};
        state.selectedMachineId = null;
        state.selectedAddons = {};
        setStep(0);
        return;
      }

      var option = event.target.closest('[data-pg-option]');
      if (option && state.step >= 1 && state.step <= 5) {
        var oid = option.getAttribute('data-pg-option');
        if (oid) {
          state.answers['q' + state.step] = oid;
          syncOptionCards(state.step);
          syncFooter();
        }
        return;
      }

      var addonLabel = event.target.closest('.sz-pg__addon');
      if (addonLabel) {
        var addonInput = addonLabel.querySelector('[data-pg-addon]');
        var addonMid = machineKey(
          addonLabel.getAttribute('data-pg-addon-machine') ||
            (addonInput && addonInput.getAttribute('data-pg-addon'))
        );
        var addonIdx = Number(
          addonLabel.getAttribute('data-pg-addon-index') ||
            (addonInput && addonInput.getAttribute('data-pg-addon-index'))
        );
        if (addonMid && !sameMachine(addonMid, state.selectedMachineId)) {
          event.preventDefault();
          selectMachine(addonMid);
          renderResults();
          return;
        }
        if (addonMid && sameMachine(addonMid, state.selectedMachineId)) {
          event.preventDefault();
          var nextChecked = !(state.selectedAddons[addonMid] && state.selectedAddons[addonMid][addonIdx]);
          selectMachine(addonMid, { addonIndex: addonIdx, addonChecked: nextChecked });
          renderResults();
          return;
        }
      }

      var selectMachineEl = event.target.closest('[data-pg-select-machine]');
      if (selectMachineEl && !event.target.closest('.sz-pg__learn, .sz-pg__addon, a')) {
        event.preventDefault();
        selectMachine(selectMachineEl.getAttribute('data-pg-select-machine'));
        renderResults();
        return;
      }

      if (event.target.closest('[data-pg-back]')) {
        event.preventDefault();
        if (state.step > 1) setStep(state.step - 1);
        return;
      }

      if (event.target.closest('[data-pg-next]')) {
        event.preventDefault();
        if (!currentAnswer(state.step)) return;
        if (state.step < 5) setStep(state.step + 1);
        else setStep(6);
        return;
      }

      var addCart = event.target.closest('[data-pg-add-cart]');
      var buyNow = event.target.closest('[data-pg-buy-now]');
      if (addCart || buyNow) {
        event.preventDefault();
        handleCheckout(!!buyNow);
      }
    });

    root.addEventListener('change', function (event) {
      var input = event.target;
      if (!input.matches('[data-pg-addon]')) return;
      var mid = machineKey(input.getAttribute('data-pg-addon'));
      var idx = Number(input.getAttribute('data-pg-addon-index'));
      if (!sameMachine(mid, state.selectedMachineId)) {
        event.preventDefault();
        selectMachine(mid);
        renderResults();
        return;
      }
      selectMachine(mid, { addonIndex: idx, addonChecked: !!input.checked });
      renderResults();
    });

    function handleCheckout(goCheckout) {
      var machine = findMachine(state.selectedMachineId);
      if (!machine || !machine.variant_id) {
        window.alert(root.dataset.needProductMsg || 'Please configure products in the theme editor.');
        return;
      }

      var items = [{ id: Number(machine.variant_id), quantity: 1 }];
      var addonIndexes = collectAddonsForMachine(machine.id);
      addonIndexes.forEach(function (idx) {
        var addon = (machine.addons || [])[idx];
        if (addon && addon.variant_id) {
          items.push({ id: Number(addon.variant_id), quantity: 1 });
        }
      });

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: items })
      })
        .then(function (res) {
          if (!res.ok) throw new Error('cart');
          return res.json();
        })
        .then(function () {
          if (goCheckout) {
            window.location.href = '/checkout';
          } else if (typeof window.dispatchEvent === 'function') {
            document.documentElement.dispatchEvent(new CustomEvent('cart:refresh'));
            window.location.href = '/cart';
          } else {
            window.location.href = '/cart';
          }
        })
        .catch(function () {
          window.alert(root.dataset.cartErrorMsg || 'Unable to add to cart. Please try again.');
        });
    }

    setStep(0);
  }

  function boot() {
    document.querySelectorAll('[data-sz-purchase-guide]').forEach(initRoot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (event) {
    var section = event.target;
    if (!section) return;
    var root = section.querySelector('[data-sz-purchase-guide]');
    if (root) {
      root.dataset.pgReady = '0';
      initRoot(root);
    }
  });
})();
